import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { useDispatch, useSelector, useStore } from 'react-redux'
import { GlobalStyles } from '@mui/material'

import { nextCssVariables } from 'common/theme/createExperienceTheme'
import { companyApi } from 'services/company'
import { ApplicationState } from 'store'
import { setUserInstanceData } from 'store/user/actions'

import {
  clearExperienceOverride,
  getExperienceOverride,
  isUiExperience,
  storeExperienceOverride
} from './experienceOverride'
import { setNextPreload } from './nextPreload'
import { ExperienceViewRegistry, UiExperience } from './types'

interface ExperienceContextValue {
  experience: UiExperience
  setExperience: (experience: UiExperience) => Promise<void>
  /** The company enabled the new experience for its users (`ui_experience_enabled`). */
  isSwitchAvailable: boolean
}

interface ExperienceRegistryContextValue {
  registry?: ExperienceViewRegistry
  hasEnabledNextViews: boolean
}

const ExperienceContext = createContext<ExperienceContextValue>({
  experience: 'legacy',
  setExperience: async () => {},
  isSwitchAvailable: false
})

const ExperienceRegistryContext = createContext<ExperienceRegistryContextValue>({
  hasEnabledNextViews: false
})

/** Set by ExperienceView to the experience its subtree actually renders with. */
export const RenderedExperienceContext = createContext<UiExperience | undefined>(undefined)

/** The experience the user chose; outside an ExperienceProvider it is always the legacy one. */
export const useExperience = () => useContext(ExperienceContext)

export const useExperienceRegistry = () => useContext(ExperienceRegistryContext)

/**
 * The experience a shared component should look like: the one of the view it is rendered in, or
 * the chosen one for global components rendered outside any view (e.g. the Notifier).
 */
export const useRenderedExperience = (): UiExperience => {
  const renderedExperience = useContext(RenderedExperienceContext)
  const { experience } = useExperience()
  return renderedExperience ?? experience
}

interface ExperienceProviderProps {
  children: ReactNode
}

export const ExperienceProvider: FC<ExperienceProviderProps> = ({ children }) => {
  const dispatch = useDispatch()
  const store = useStore<ApplicationState>()
  const preference = useSelector((state: ApplicationState) => state.user.instanceData?.uiExperience)
  const isSwitchAvailable = useSelector(
    (state: ApplicationState) => state.user.instanceData?.uiExperienceEnabled === true
  )
  const mode = useSelector((state: ApplicationState) => state.layout.theme)
  const [override, setOverride] = useState(getExperienceOverride)

  const experience =
    override ?? (isSwitchAvailable && isUiExperience(preference) ? preference : 'legacy')

  const updateStoredExperience = useCallback(
    (uiExperience: string | undefined) => {
      const instanceData = store.getState().user.instanceData
      if (instanceData) {
        dispatch(setUserInstanceData({ ...instanceData, uiExperience }))
      }
    },
    [dispatch, store]
  )

  const setExperience = useCallback(
    async (newExperience: UiExperience) => {
      const previous = store.getState().user.instanceData?.uiExperience
      const previousOverride = getExperienceOverride()
      clearExperienceOverride()
      setOverride(undefined)
      updateStoredExperience(newExperience)
      try {
        await companyApi.setSelfServicePreference({ code: 'ui_experience', value: newExperience })
      } catch (error) {
        updateStoredExperience(previous)
        if (previousOverride) {
          storeExperienceOverride(previousOverride)
          setOverride(previousOverride)
        }
        throw error
      }
    },
    [store, updateStoredExperience]
  )

  useEffect(() => {
    setNextPreload(experience === 'next')
  }, [experience])

  const value = useMemo(() => ({ experience, setExperience, isSwitchAvailable }), [
    experience,
    setExperience,
    isSwitchAvailable
  ])

  return (
    <ExperienceContext.Provider value={value}>
      {experience === 'next' && <GlobalStyles styles={{ ':root': nextCssVariables(mode) }} />}
      {children}
    </ExperienceContext.Provider>
  )
}

interface ExperienceRegistryProviderProps {
  registry: ExperienceViewRegistry
  children: ReactNode
}

/** Kept apart from ExperienceProvider so the views it imports stay out of the entry bundle. */
export const ExperienceRegistryProvider: FC<ExperienceRegistryProviderProps> = ({
  registry,
  children
}) => {
  const value = useMemo(
    () => ({
      registry,
      hasEnabledNextViews: Object.values(registry).some(({ next, nextDisabled }) =>
        Boolean(next && !nextDisabled)
      )
    }),
    [registry]
  )
  return (
    <ExperienceRegistryContext.Provider value={value}>
      {children}
    </ExperienceRegistryContext.Provider>
  )
}
